import { useState, useEffect, useCallback } from "react"
import { useSession } from "next-auth/react"

export type ThemeStyle = "tesla" | "legacy"

type UserPreferences = {
  language?: "en" | "pt"
  hiddenMenuItems?: string[]
  menuLayout?: "side" | "horizontal"
  themeStyle?: ThemeStyle
  /** When true, search results hide any property missing base data or a fair price estimate -
   * same criteria as the warning icon on the property card. Default false. */
  hidePropertiesWithoutEvaluation?: boolean
  /** Custom prompt for building consultant analysis ("O meu perfil de analista"). Used for all properties. */
  analystProfilePrompt?: string
  updatedAt?: string
}

const STORAGE_KEY = "odynx:userPreferences"
const STORAGE_CACHE_TTL = 5 * 60 * 1000 // 5 minutes

type CachedPreferences = { data: UserPreferences; timestamp: number }

export const useUserPreferences = () => {
  const { data: session } = useSession()
  const [preferences, setPreferences] = useState<UserPreferences>({
    language: "en",
    hiddenMenuItems: [],
    menuLayout: "side",
    themeStyle: "tesla"
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchPreferences = useCallback(async () => {
    if (!session?.user?.email) {
      setLoading(false)
      // Use defaults if no session
      setPreferences({
        language: "en",
        hiddenMenuItems: [],
        menuLayout: "side",
        themeStyle: "tesla",
        hidePropertiesWithoutEvaluation: false,
        analystProfilePrompt: ""
      })
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Check cache first
      const cacheKey = `${STORAGE_KEY}:${session.user.email}`
      if (typeof window !== "undefined") {
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          try {
            const parsed: CachedPreferences = JSON.parse(cached)
            const age = Date.now() - parsed.timestamp
            if (age < STORAGE_CACHE_TTL) {
              setPreferences(parsed.data)
              setLoading(false)
              // Still fetch in background to update cache
            } else {
              // Cache expired, fetch fresh
            }
          } catch {
            // Invalid cache, continue to fetch
          }
        }
      }

      const response = await fetch("/api/user/preferences", { method: "GET", headers: { "Content-Type": "application/json" } })

      if (!response.ok) {
        let errorMessage = "Failed to fetch preferences"
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const data = await response.json()
      const prefs: UserPreferences = {
        language: data.language || "en",
        hiddenMenuItems: data.hiddenMenuItems || [],
        menuLayout: data.menuLayout || "side",
        themeStyle: data.themeStyle === "legacy" ? "legacy" : "tesla",
        hidePropertiesWithoutEvaluation: data.hidePropertiesWithoutEvaluation === true,
        analystProfilePrompt: typeof data.analystProfilePrompt === "string" ? data.analystProfilePrompt : ""
      }

      setPreferences(prefs)

      // Update cache
      if (typeof window !== "undefined") {
        const cache: CachedPreferences = { data: prefs, timestamp: Date.now() }
        localStorage.setItem(cacheKey, JSON.stringify(cache))
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load preferences"
      console.error("Error fetching user preferences:", err)
      setError(errorMessage)
      // Use defaults on error
      setPreferences({
        language: "en",
        hiddenMenuItems: [],
        menuLayout: "side",
        themeStyle: "tesla",
        hidePropertiesWithoutEvaluation: false,
        analystProfilePrompt: ""
      })
    } finally {
      setLoading(false)
    }
  }, [session?.user?.email])

  const savePreferences = useCallback(
    async (newPreferences: UserPreferences) => {
      if (!session?.user?.email) {
        throw new Error("User not authenticated")
      }

      try {
        setError(null)

        const response = await fetch("/api/user/preferences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(newPreferences)
        })

        if (!response.ok) {
          let errorMessage = "Failed to save preferences"
          try {
            const errorData = (await response.json()) as { error?: string }
            errorMessage = errorData.error || errorMessage
          } catch {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`
          }
          throw new Error(errorMessage)
        }

        setPreferences(newPreferences)

        // Update cache
        if (typeof window !== "undefined") {
          const cacheKey = `${STORAGE_KEY}:${session.user.email}`
          const cache: CachedPreferences = { data: newPreferences, timestamp: Date.now() }
          localStorage.setItem(cacheKey, JSON.stringify(cache))
        }

        return true
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : "Failed to save preferences"
        console.error("Error saving user preferences:", err)
        setError(errorMessage)
        throw err
      }
    },
    [session?.user?.email]
  )

  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  return { preferences, loading, error, savePreferences, refetch: fetchPreferences }
}
